import { useEffect, useMemo } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TYPES } from "@utils/inversifyJS/types";
import type { GetMyPermissionsService } from "@application/useCases/workspace/GetMyPermissionsService";
import { selectPermissionEntry, toPermissionScopeKey, usePermissionStore } from "@presentation/stores/PermissionStore";
import { isProjectActionAllowedByRole, isTeamActionAllowedByRole } from "@domain/valueObjects/RoleActionMatrix";
import { ADMIN_ROLE_NAME, ProjectActions, TeamActions } from "@domain/schemas/workspace";
import type { MyTeam, PermissionScope, ProjectAction, TeamAction } from "@domain/schemas/workspace";
import type { PermissionEntry } from "@presentation/stores/PermissionStore";

const CONTEXT_MANAGEMENT_TEAM_ACTIONS: ReadonlySet<TeamAction> = new Set([TeamActions.CreateProject, TeamActions.DeleteProject, TeamActions.RenameProject, TeamActions.RenameTeam]);
const CONTEXT_MANAGEMENT_PROJECT_ACTIONS: ReadonlySet<ProjectAction> = new Set([ProjectActions.Rename]);

/**
 * `'entitlement'` is resolved client-side (WP8), not from the server: `GET /me/permissions`
 * carries no entitlement flag (entitlement is applied server-side by filtering the action arrays
 * - see `MePermissions`'s own doc comment), so a denial is reclassified as `'entitlement'` only
 * when the role held *would* grant the action per the static `RoleActionMatrix` and either the
 * action always requires Context Management, or the context is confirmed non-default.
 */
export type PermissionDenialReason = "entitlement" | "permission" | null;

export interface PermissionCheckResult {
  allowed: boolean;
  reason: PermissionDenialReason;
}

const loadScopedPermissions = async (scope: PermissionScope, service: GetMyPermissionsService) => {
  if (selectPermissionEntry(scope)(usePermissionStore.getState()).status !== "idle") return;
  usePermissionStore.getState().markLoading(scope);
  try {
    const permissions = await service.getMyPermissionsAsync(scope);
    usePermissionStore.getState().setPermissions(scope, permissions);
  } catch (error) {
    usePermissionStore.getState().setError(scope, error);
  }
};

/**
 * Fetches (once, de-duplicated across every caller checking the same scope) and reads the
 * resolved grants for an arbitrary scope, not just the app's currently-active one - e.g. a row in
 * a team's project inventory that is not the active project.
 */
const useScopedPermissionEntry = (scope: PermissionScope, hasId: boolean) => {
  const getMyPermissionsService = useInjection<GetMyPermissionsService>(TYPES.GetMyPermissionsService);
  const entry = usePermissionStore(selectPermissionEntry(scope));
  const refetchNonce = usePermissionStore((state) => state.refetchNonce);
  const scopeKey = toPermissionScopeKey(scope);

  useEffect(() => {
    if (!hasId) return;
    // Read fresh rather than trust the closed-over `entry`: multiple components checking the
    // same scope in the same commit must not each kick off their own redundant fetch.
    // Deliberately not gated on a per-effect `isCancelled` flag: under StrictMode's dev-only
    // double-invoke, the first instance's cleanup would flip that flag before this promise
    // settles, and the second instance skips fetching entirely (status is already "loading") -
    // together that left the entry stuck "loading" forever with its answer discarded. Writing to
    // the keyed global store is idempotent per scope, so applying a late result is always safe.
    void loadScopedPermissions(scope, getMyPermissionsService);
  }, [hasId, scopeKey, refetchNonce, getMyPermissionsService]);

  return entry;
};

/**
 * Pre-flight affordance check for a `TeamAction` against an arbitrary team - the frontend
 * rendering contract describes: a control not granted by the caller's role is not rendered at
 * all, EXCEPT when the entitlement policy blocks an action that the role (or Admin) would
 * otherwise grant. Non-structural actions are entitlement-gated only when `isDefault` is
 * confirmed false; omitting it keeps the normal hide-only behavior while context details are
 * unresolved.
 */
export const useTeamPermission = (teamId: string | null | undefined, action: TeamAction, isDefault?: boolean): PermissionCheckResult => {
  const hasId = Boolean(teamId);
  const scope = useMemo((): PermissionScope => (hasId ? { teamId: teamId! } : {}), [hasId, teamId]);
  const entry = useScopedPermissionEntry(scope, hasId);

  return resolveTeamPermission(hasId ? entry : undefined, action, isDefault);
};

const resolveTeamPermission = (entry: PermissionEntry | undefined, action: TeamAction, isDefault?: boolean): PermissionCheckResult => {
  if (entry?.status !== "loaded" || !entry.permissions) return { allowed: false, reason: null };

  const { isAdmin, teamRole, teamActions } = entry.permissions;
  const allowed = teamActions.includes(action);
  if (allowed) return { allowed: true, reason: null };

  const roleWouldAllow = isAdmin || teamRole === ADMIN_ROLE_NAME || (teamRole !== null && isTeamActionAllowedByRole(teamRole, action));
  const requiresEntitlement = CONTEXT_MANAGEMENT_TEAM_ACTIONS.has(action) || isDefault === false;
  const isEntitlementGap = requiresEntitlement && roleWouldAllow;
  return { allowed: false, reason: isEntitlementGap ? "entitlement" : "permission" };
};

export const useTeamPermissions = (teams: readonly MyTeam[], action: TeamAction) => {
  const service = useInjection<GetMyPermissionsService>(TYPES.GetMyPermissionsService);
  const entries = usePermissionStore((state) => state.entries);
  const refetchNonce = usePermissionStore((state) => state.refetchNonce);

  useEffect(() => {
    for (const team of teams) {
      void loadScopedPermissions({ teamId: team.id }, service);
    }
  }, [teams, service, refetchNonce]);

  return teams.map((team) => ({
    team,
    permission: resolveTeamPermission(entries[toPermissionScopeKey({ teamId: team.id })], action, team.isDefault),
  }));
};

/**
Project-scope counterpart of {@link useTeamPermission}, with the same caveats (including the `isDefault` parameter).
*/
export const useProjectPermission = (projectId: string | null | undefined, action: ProjectAction, isDefault?: boolean): PermissionCheckResult => {
  const hasId = Boolean(projectId);
  const scope = useMemo((): PermissionScope => (hasId ? { projectId: projectId! } : {}), [hasId, projectId]);
  const entry = useScopedPermissionEntry(scope, hasId);

  if (!hasId || entry.status !== "loaded" || !entry.permissions) return { allowed: false, reason: null };

  const { isAdmin, projectRole, projectActions } = entry.permissions;
  const allowed = projectActions.includes(action);
  if (allowed) return { allowed: true, reason: null };

  const roleWouldAllow = isAdmin || projectRole === ADMIN_ROLE_NAME || (projectRole !== null && isProjectActionAllowedByRole(projectRole, action));
  const requiresEntitlement = CONTEXT_MANAGEMENT_PROJECT_ACTIONS.has(action) || isDefault === false;
  const isEntitlementGap = requiresEntitlement && roleWouldAllow;
  return { allowed: false, reason: isEntitlementGap ? "entitlement" : "permission" };
};
